import { forwardRef, useState, type KeyboardEvent, type InputHTMLAttributes } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * TagInput Component
 * Based on UX Design Guidelines §7.2 - Form Inputs
 * Used for keyword rule creation (FLOW-03 §2.2)
 *
 * Features:
 * - Enter key to add tag
 * - Backspace to remove last tag
 * - Click X to remove specific tag
 * - Max tags limit support
 */

export interface TagInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: string[]
  onChange: (tags: string[]) => void
  label?: string
  error?: string
  hint?: string
  maxTags?: number
}

export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      value = [],
      onChange,
      label,
      error,
      hint,
      maxTags,
      placeholder = 'Въведете и натиснете Enter',
      disabled,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState('')
    const inputId = id || props.name
    const hasError = Boolean(error)
    const isAtLimit = maxTags !== undefined && value.length >= maxTags

    const addTag = (tag: string) => {
      const trimmedTag = tag.trim()
      if (!trimmedTag) return
      if (value.includes(trimmedTag)) return
      if (isAtLimit) return

      onChange([...value, trimmedTag])
      setInputValue('')
    }

    const removeTag = (tagToRemove: string) => {
      onChange(value.filter((tag) => tag !== tagToRemove))
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        addTag(inputValue)
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        removeTag(value[value.length - 1])
      }
    }

    return (
      <div className={cn('w-full', className)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-body-sm font-medium text-gray-700 mb-1.5"
          >
            {label}
            {props.required && (
              <span className="text-error ml-0.5" aria-hidden="true">*</span>
            )}
          </label>
        )}

        {/* Input container */}
        <div
          className={cn(
            'flex flex-wrap items-center gap-2 p-2',
            'min-h-[48px] bg-white border rounded-md',
            'transition-colors duration-small',
            'focus-within:ring-2 focus-within:ring-offset-0',
            hasError
              ? 'border-error focus-within:ring-error/40'
              : 'border-gray-300 focus-within:ring-accent/40',
            disabled && 'bg-gray-50 cursor-not-allowed'
          )}
        >
          {/* Tags */}
          {value.map((tag) => (
            <span
              key={tag}
              className={cn(
                'inline-flex items-center gap-1 px-2.5 py-1',
                'bg-accent/10 text-accent rounded-md',
                'text-body-sm font-medium'
              )}
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="text-accent/70 hover:text-accent focus:outline-none"
                  aria-label={`Премахни ${tag}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}

          {/* Input */}
          {!isAtLimit && (
            <input
              ref={ref}
              id={inputId}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => addTag(inputValue)}
              placeholder={value.length === 0 ? placeholder : ''}
              disabled={disabled}
              aria-invalid={hasError}
              aria-describedby={
                error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
              }
              className={cn(
                'flex-1 min-w-[120px] h-8 px-2',
                'text-body text-gray-900 bg-transparent',
                'placeholder:text-gray-400',
                'focus:outline-none',
                disabled && 'cursor-not-allowed'
              )}
              {...props}
            />
          )}
        </div>

        {/* Tag count */}
        {maxTags && (
          <p className="mt-1 text-tiny text-gray-500 text-right">
            {value.length} / {maxTags}
          </p>
        )}

        {/* Error message */}
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1.5 text-caption text-error"
            role="alert"
          >
            {error}
          </p>
        )}

        {/* Hint text */}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="mt-1.5 text-caption text-gray-500">
            {hint}
          </p>
        )}
      </div>
    )
  }
)

TagInput.displayName = 'TagInput'
